import { BehaviorSubject, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { FileSystemService } from '#services/file-system/file-system.service';
import { Capacitor } from '@capacitor/core';

import {
  LyfeVaultDocument,
  LyfeVaultDocumentRequest,
  LyfeVaultDocumentResponse,
  LyfeVaultDocumentsResponse,
} from '#models/db/lyfe-vault/lyfe-vault.model';
import { ApiRepositoryService } from '#repositories/api-repository/api-repository.service';
import { IntermediaryService, MS_DELAY } from '#services/intermediary/intermediary.service';
import { ILyfeVaultService } from './lyfe-vault.interface';
import { logger } from '#lib/logger';
import { BucketName } from '#models/db/asset/asset.model';

@Injectable({
  providedIn: 'root',
})
export class LyfeVaultService implements ILyfeVaultService {
  path = 'documents';

  createLyfeVaultLoading$ = new Subject<boolean>();
  getLyfeVaultLoading$ = new Subject<boolean>();
  updateLyfeVaultLoading$ = new Subject<boolean>();
  activeLyfeVault$ = new BehaviorSubject<LyfeVaultDocument | null>(null);

  deleteLyfeVaultLoading$ = new Subject<boolean>();

  getLyfeVaultsLoading$ = new Subject<boolean>();
  lyfeVaults$ = new BehaviorSubject<LyfeVaultDocument[] | null>(null);

  constructor(
    private apiRepository: ApiRepositoryService,
    private intermediaryHandler: IntermediaryService,
    private fileSystemService: FileSystemService
  ) {}

  createLyfeVaultDocument(
    request: LyfeVaultDocumentRequest,
    nextFn?: (data: LyfeVaultDocument | null) => void
  ) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.createLyfeVaultLoading$,
        subject: this.activeLyfeVault$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.post<
          LyfeVaultDocumentResponse,
          LyfeVaultDocumentRequest
        >(
          {
            url: this.path,
          },
          request
        );

        logger.log(response, 'LyfeVaultService.createLyfeVaultDocument -> response');

        return response.details;
      }
    );
  }

  getLyfeVaultDocuments(nextFn?: (data: LyfeVaultDocument[] | null) => void, silent?: boolean) {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.getLyfeVaultsLoading$ : undefined,
        subject: this.lyfeVaults$,
        delay: !silent ? MS_DELAY : undefined,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.get<LyfeVaultDocumentsResponse>({
          url: this.path,
        });

        logger.log(response.details, 'LyfeVaultService.getLyfeVaultDocuments -> response');

        return response.details;
      }
    );
  }

  getLyfeVaultDocumentById(
    id: string,
    nextFn?: (data: LyfeVaultDocument | null) => void,
    silent?: boolean
  ) {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.getLyfeVaultLoading$ : undefined,
        subject: this.activeLyfeVault$,
        delay: !silent ? MS_DELAY : undefined,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.get<LyfeVaultDocumentResponse>({
          url: `${this.path}/${id}`,
        });

        logger.log(response.details, 'LyfeVaultService.getLyfeVaultDocumentById -> response');

        return response.details;
      }
    );
  }

  updateLyfeVaultDocument(
    request: LyfeVaultDocumentRequest & { id: string },
    nextFn?: (data: LyfeVaultDocument | null) => void,
    silent?: boolean
  ) {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.updateLyfeVaultLoading$ : undefined,
        subject: this.activeLyfeVault$,
        delay: !silent ? MS_DELAY : undefined,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.patch<
          LyfeVaultDocumentResponse,
          LyfeVaultDocumentRequest
        >(
          {
            url: `${this.path}/${request.id}`,
          },
          request
        );

        logger.log(response.details, 'LyfeVaultService.updateLyfeVaultDocument -> response');

        return response.details;
      }
    );
  }

  deleteLyfeVaultDocument(
    id: string,
    path: string | null,
    nextFn?: (data: LyfeVaultDocument | null) => void
  ) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.deleteLyfeVaultLoading$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.delete<LyfeVaultDocumentResponse>({
          url: `${this.path}/${id}`,
        });

        logger.log(response, 'LyfeVaultService.deleteLyfeVaultDocument -> response');

        // If we have a path and are on a native platform, remove the file from the local file system.
        if (path && Capacitor.isNativePlatform()) {
          const bucketName: BucketName = 'vault-asset-uploads';

          await this.fileSystemService.removeFile(`${bucketName}/${path}`);
        }

        return response.details;
      }
    );
  }

  resetLyfeVault() {
    this.activeLyfeVault$.next(null);
    this.lyfeVaults$.next(null);
    logger.log('complete', 'LyfeVaultService.resetLyfeVault');
  }
}
